// Lucide icons (lucide.dev) as the Neomaris v2 design system uses them: stroke
// icons on a 24px grid, stroke 2, round caps and joins, currentColor. The first
// group is copied from the design system's components/icons/Icon.jsx; the rest
// are the same Lucide glyphs added for the website. These are the navigation
// and action icons; the filled instrument symbols are InstrumentIcon, below
// (Marina, TOM-677).
import { useId, type CSSProperties, type ReactElement } from 'react';

const PATHS: Record<string, ReactElement> = {
  tombolo: (
    <>
      <path d="M12 3.2 20.8 20 12 16.5 3.2 20 12 3.2Z" />
      <path d="m12 7.7 3.7 7.1-3.7-1.5-3.7 1.5L12 7.7Z" />
    </>
  ),
  chats: (
    <>
      <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
      <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" />
    </>
  ),
  anchor: (
    <>
      <circle cx="12" cy="5" r="3" />
      <path d="M12 22V8" />
      <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
    </>
  ),
  water: (
    <path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7Z" />
  ),
  engine: <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />,
  friend: (
    <>
      <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
      <path d="M16.24 7.76A6 6 0 1 0 8.23 16.67" />
      <circle cx="12" cy="12" r="2" />
      <path d="m13.41 10.59 5.66-5.66" />
    </>
  ),
  reports: (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8M16 13H8M16 17H8" />
    </>
  ),
  alarm: (
    <>
      <path d="M7 18v-6a5 5 0 1 1 10 0v6" />
      <path d="M5 21a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-1a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2z" />
      <path d="M21 12h1M2 12h1M12 2v1M18.5 4.5 18 5M5.5 4.5 6 5" />
    </>
  ),
  // Lucide's bell-plus: the alarm composer, where a sparkle would read as
  // Neo's chat (TOM-568).
  'bell-plus': (
    <>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M15 8h6" />
      <path d="M18 5v6" />
      <path d="M20.002 14.464a9 9 0 0 0 .738.863A1 1 0 0 1 20 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 8.75-5.332" />
    </>
  ),
  boards: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  // The left sidebar closed to its icons, or opened to its labels (Lucide's
  // panel-left-close and panel-left-open).
  'panel-left-close': (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="m16 15-3-3 3-3" />
    </>
  ),
  'panel-left-open': (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 3v18" />
      <path d="m14 9 3 3-3 3" />
    </>
  ),
  'panel-right-close': (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M15 3v18" />
      <path d="m8 9 3 3-3 3" />
    </>
  ),
  more: (
    <>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </>
  ),
  underway: (
    <>
      <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M19.4 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.9 5.3 2.8 7.8" />
      <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
      <path d="M12 10v4M12 2v3" />
    </>
  ),
  // A boat in its slip, seen bow-on, Mike's drawing (TOM-955). Its detail is
  // drawn at stroke 1.5, so it keeps that whatever the caller passes.
  docked: (
    <g strokeWidth={1.5}>
      <g transform="translate(0 -3)">
        <path d="M2.5 8.5Q12 10.5 21.5 8.5l-.5 1.8c-2 .7-3.9 1.2-6 1.5V14c1 .1 1.8.6 1.8 1.1 0 1.2-9.6 1.2-9.6 0 0-.5.8-1 1.8-1.1v-2.2c-2.1-.3-4-.8-6-1.5Z" />
        <ellipse cx="12" cy="13.1" rx="1.5" ry="1.2" />
      </g>
      <path d="M3 11h4m10 0h4l1 7H2l1-7m-1 7v2h20v-2M12 15v3" />
      <path d="M3 22.5q2-1 4 0t4 0t4 0t4 0" />
    </g>
  ),
  auto: (
    <>
      <path d="M21 12a9 9 0 1 1-3-6.7L21 8" />
      <path d="M21 3v5h-5" />
    </>
  ),
  'new-chat': (
    <>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6M9 10h6" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  'sign-out': (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>
  ),
  tide: (
    <>
      <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
      <path d="M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
    </>
  ),
  wind: (
    <path d="M12.8 19.6A2 2 0 1 0 14 16H2M17.5 8a2.5 2.5 0 1 1 2 4H2M9.8 4.4A2 2 0 1 1 11 8H2" />
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </>
  ),
  moon: <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />,
  plus: <path d="M12 5v14M5 12h14" />,
  mic: (
    <>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <path d="M12 19v3" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  'chevron-right': <path d="m9 18 6-6-6-6" />,
  'chevron-left': <path d="m15 18-6-6 6-6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  'arrow-up': <path d="M12 19V5M5 12l7-7 7 7" />,
  'arrow-down': <path d="M12 5v14M19 12l-7 7-7-7" />,
  // The sparkle while the chat it opened is in: it slides the chat back out (TOM-738).
  'arrow-right': <path d="M5 12h14M12 5l7 7-7 7" />,
  'chevron-up': <path d="m18 15-6-6-6 6" />,
  battery: (
    <>
      <rect width="16" height="10" x="2" y="7" rx="2" />
      <path d="M22 11v2M6 11v2M10 11v2" />
    </>
  ),
  // Instrument card glyphs (TOM-194), from Lucide: thermometer (the same glyph
  // as `engine`), zap, fuel, navigation and activity.
  thermometer: <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0Z" />,
  zap: (
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  ),
  fuel: (
    <>
      <path d="M3 22h12M4 9h10" />
      <path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" />
      <path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5" />
    </>
  ),
  navigation: <path d="M3 11 22 2l-9 19-2-8-8-2Z" />,
  activity: (
    <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
  ),
  download: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5" />
      <path d="M12 15V3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </>
  ),
  gauge: (
    <>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </>
  ),
  // The lines of Neo's receipt in the chat (TOM-354).
  pencil: (
    <>
      <path d="M21.17 6.81a1 1 0 0 0-3.99-3.99L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" />
      <path d="m15 5 4 4" />
    </>
  ),
  // A board item's Edit page (TOM-622): Lucide's code.
  code: (
    <>
      <path d="m16 18 6-6-6-6" />
      <path d="m8 6-6 6 6 6" />
    </>
  ),
  minus: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8" />
    </>
  ),
  move: <path d="M12 2v20M8 18l4 4 4-4M8 6l4-4 4 4" />,
  // Lucide's text-align-start, -center and -end, for an item's heading (TOM-986).
  'align-left': <path d="M21 5H3M15 12H3M17 19H3" />,
  'align-center': <path d="M21 5H3M17 12H7M19 19H5" />,
  'align-right': <path d="M21 5H3M21 12H9M21 19H7" />,
  // First drawn for the quick-readings picker (TOM-355), which left in TOM-526.
  grip: (
    <>
      <circle cx="9" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="18" r="1" />
    </>
  ),
  'plus-circle': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8M12 8v8" />
    </>
  ),
  sparkles: (
    <>
      <path d="M9.94 15.5A2 2 0 0 0 8.5 14.06l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0L14.06 8.5A2 2 0 0 0 15.5 9.94l6.14 1.58a.5.5 0 0 1 0 .96L15.5 14.06a2 2 0 0 0-1.44 1.44l-1.58 6.13a.5.5 0 0 1-.96 0z" />
      <path d="M20 3v4M22 5h-4" />
    </>
  ),
  expand: <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />,
  // The board's second maximize step, true full screen (TOM-527): Lucide maximize.
  maximize: (
    <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
  ),
  // Out of the board's maximize (TOM-527): Lucide minimize-2.
  minimize: <path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" />,
  undo: (
    <>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </>
  ),
  // Hide, on a system board's item (TOM-358): an eye with a line through it.
  // A board item's "Not on this boat" (TOM-409): the equipment is not aboard.
  'circle-slash': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m5.6 5.6 12.8 12.8" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="m2 2 20 20" />
    </>
  ),
  // A map in Neo's answer and on a board (TOM-468): Lucide's map.
  map: (
    <>
      <path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
      <path d="M15 5.764v15" />
      <path d="M9 3.236v15" />
    </>
  ),
  // A diagram Neo showed (TOM-643): Lucide's workflow, two boxes joined.
  diagram: (
    <>
      <rect width="8" height="8" x="3" y="3" rx="2" />
      <path d="M7 11v4a2 2 0 0 0 2 2h4" />
      <rect width="8" height="8" x="13" y="13" rx="2" />
    </>
  ),
  // A picture Neo drew (TOM-646): Lucide's image.
  picture: (
    <>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </>
  ),
  // The boat's notebook in Neo's receipt (TOM-611): Lucide's book-open.
  notebook: (
    <>
      <path d="M12 7v14" />
      <path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
    </>
  ),
  // The board beside the chat, shown or hidden (TOM-414): Lucide's panel-right,
  // a rectangle with a panel along one side.
  'panel-right': (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M15 3v18" />
    </>
  ),
  // Copy one of Neo's answers (TOM-709): Lucide's copy.
  copy: (
    <>
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </>
  ),
  // Before an answer's source line (TOM-709): Lucide's info.
  info: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </>
  ),
};

export type IconName = keyof typeof PATHS;

export function Icon({
  name,
  size = 22,
  strokeWidth = 2,
  title,
  className,
  style,
}: {
  name: string;
  size?: number;
  strokeWidth?: number;
  title?: string;
  className?: string;
  style?: CSSProperties;
}) {
  const glyph = PATHS[name];
  if (!glyph) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={className}
      style={{ flex: 'none', display: 'block', ...style }}
    >
      {glyph}
    </svg>
  );
}

/**
 * The brand's N with the wave through it (TOM-812), from the source of truth at
 * docs/design/brand/neomaris-mark.svg. Filled in `currentColor`, so the caller
 * colours it; the wave is the gap between the shapes, so the background shows
 * through. The viewBox is cropped to the glyph so it centres in its box.
 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="53 59 400 400"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <g transform="translate(0,512) scale(0.1,-0.1)">
        <path d="M1140 3121 c0 -723 -3 -1018 -11 -1042 -7 -19 -30 -53 -53 -77 -110 -115 -276 -133 -493 -55 -46 16 37 -51 102 -83 211 -103 419 -55 612 143 126 128 188 237 427 737 164 343 278 466 436 466 162 0 282 -151 411 -515 117 -330 205 -471 284 -451 49 12 145 117 145 158 0 11 -52 101 -116 201 -64 100 -164 256 -222 347 -58 91 -144 226 -192 300 -48 74 -155 241 -237 370 -82 129 -189 297 -237 373 l-88 137 -384 0 -384 0 0 -1009z" />
        <path d="M3110 4103 c0 -16 -1 -360 -3 -765 -1 -406 0 -738 3 -738 4 0 39 71 79 158 236 506 413 734 659 848 l72 34 -2 242 -3 243 -402 3 -403 2 0 -27z" />
        <path d="M4028 3495 c-245 -62 -415 -254 -639 -722 -283 -591 -371 -703 -554 -703 -164 1 -260 125 -420 550 -127 334 -180 420 -265 420 -31 0 -105 -74 -156 -156 l-46 -74 213 -327 c117 -181 280 -431 362 -558 82 -126 259 -399 393 -606 240 -370 244 -376 281 -383 49 -8 671 -8 701 0 l22 6 0 1163 0 1162 29 41 c87 119 337 147 512 57 51 -27 31 -1 -35 43 -124 84 -277 118 -398 87z" />
        <path d="M1855 2614 c-201 -427 -299 -587 -447 -736 -68 -68 -183 -150 -238 -170 l-30 -10 0 -379 0 -378 33 -6 c47 -9 750 -1 765 8 13 9 17 1785 3 1821 -5 14 -27 -24 -86 -150z" />
      </g>
    </svg>
  );
}

/** The app icon: the brand's N on a rounded tile in the theme's accent (TOM-812). */
export function AppMark({ size = 30 }: { size?: number }) {
  return (
    <span className="tb-app-mark" style={{ width: size, height: size }} aria-hidden="true">
      <LogoMark />
    </span>
  );
}

/**
 * The filled instrument symbols (Marina, DESIGN_SYSTEM.md §4, TOM-677): solid
 * glyphs for what a card or the header reads, kept apart from the outline
 * navigation icons above. Bolt, pump, gear and pin fill in `currentColor`, so
 * the caller colours them by kind (`--tm-kind-power-icon`); the droplet, sun,
 * cloud and moon carry their own highlight and shading from the theme's
 * tokens. Night's tokens draw them all in its red. Decorative: hidden from
 * assistive technology unless given a `title`.
 */
const INSTRUMENT_GLYPHS = {
  bolt: () => <path fill="currentColor" d="m13 2-9 12h7l-1 8 10-13h-8Z" />,
  droplet: (id: string) => (
    <>
      <defs>
        <linearGradient id={`${id}-water`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--tm-water-highlight)" />
          <stop offset="1" stopColor="currentColor" />
        </linearGradient>
      </defs>
      <path fill={`url(#${id}-water)`} d="M12 1C10 6 4 11 4 16a8 8 0 0 0 16 0c0-5-6-10-8-15Z" />
      <path
        d="M7 14c0-2 1-3 2-5"
        fill="none"
        stroke="var(--tm-water-highlight)"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity=".85"
      />
    </>
  ),
  pump: () => (
    <>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M4 2h9a1 1 0 0 1 1 1v17h1v2H2v-2h1V3a1 1 0 0 1 1-1Zm1 3v6h7V5H5Z"
      />
      <path
        d="M14 12h2v6a2.5 2.5 0 0 0 5 0V8l-4-4m1 1-1 3 4 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  gear: () => (
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M19.95 9.28L22.79 9.85L22.79 14.15L19.95 14.72L19.54 15.70L21.15 18.11L18.11 21.15L15.70 19.54L14.72 19.95L14.15 22.79L9.85 22.79L9.28 19.95L8.30 19.54L5.89 21.15L2.85 18.11L4.46 15.70L4.05 14.72L1.21 14.15L1.21 9.85L4.05 9.28L4.46 8.30L2.85 5.89L5.89 2.85L8.30 4.46L9.28 4.05L9.85 1.21L14.15 1.21L14.72 4.05L15.70 4.46L18.11 2.85L21.15 5.89L19.54 8.30Z M16 12a4 4 0 1 0-8 0a4 4 0 1 0 8 0Z"
    />
  ),
  sun: (id: string) => (
    <>
      <defs>
        <linearGradient id={`${id}-sun`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--tm-sun-highlight)" />
          <stop offset="1" stopColor="var(--tm-sun)" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="5.5" fill={`url(#${id}-sun)`} />
      <path
        d="M12 1v2m0 18v2M1 12h2m18 0h2M4 4l2 2m12 12 2 2M4 20l2-2M18 6l2-2"
        fill="none"
        stroke="var(--tm-sun)"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </>
  ),
  cloud: (id: string) => (
    <>
      <defs>
        <linearGradient id={`${id}-cloud`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--tm-cloud-highlight)" />
          <stop offset="1" stopColor="var(--tm-cloud-shadow)" />
        </linearGradient>
      </defs>
      <path
        d="M6 19a5 5 0 0 1-1-10 7 7 0 0 1 13-1 5.5 5.5 0 0 1 0 11Z"
        fill={`url(#${id}-cloud)`}
        stroke="var(--tm-cloud-edge)"
        strokeWidth=".8"
      />
    </>
  ),
  'partly-cloudy': (id: string) => (
    <>
      <defs>
        <linearGradient id={`${id}-sun`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--tm-sun-highlight)" />
          <stop offset="1" stopColor="var(--tm-sun)" />
        </linearGradient>
        <linearGradient id={`${id}-cloud`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--tm-cloud-highlight)" />
          <stop offset="1" stopColor="var(--tm-cloud-shadow)" />
        </linearGradient>
      </defs>
      <g transform="scale(.75)">
        <circle cx="21" cy="11" r="7" fill={`url(#${id}-sun)`} />
        <path
          d="M21 1v2m0 16v2M11 11h2m16 0h2M14 4l1.5 1.5M27 17l1.5 1.5M14 18l1.5-1.5M27 5l1.5-1.5"
          fill="none"
          stroke="var(--tm-sun)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M8 28a6 6 0 0 1-1-12 8 8 0 0 1 15-1 6.5 6.5 0 0 1 0 13Z"
          fill={`url(#${id}-cloud)`}
          stroke="var(--tm-cloud-edge)"
          strokeWidth=".8"
        />
      </g>
    </>
  ),
  moon: () => <path fill="var(--tm-moon)" d="M20 15A9 9 0 0 1 9 4a9 9 0 1 0 11 11Z" />,
  pin: () => (
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M20 10c0 6-8 13-8 13S4 16 4 10a8 8 0 0 1 16 0Z M14.5 10a2.5 2.5 0 1 0-5 0a2.5 2.5 0 1 0 5 0Z"
    />
  ),
} satisfies Record<string, (id: string) => ReactElement>;

export type InstrumentIconName = keyof typeof INSTRUMENT_GLYPHS;

export function InstrumentIcon({
  name,
  size = 22,
  title,
  className,
  style,
}: {
  name: InstrumentIconName;
  size?: number;
  title?: string;
  className?: string;
  style?: CSSProperties;
}) {
  // Each drawing's gradients need ids of their own on a page with many icons.
  const id = useId().replace(/:/g, '');
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={`tb-instrument-icon${className === undefined ? '' : ` ${className}`}`}
      style={{ flex: 'none', display: 'block', ...style }}
      data-icon={name}
    >
      {INSTRUMENT_GLYPHS[name](id)}
    </svg>
  );
}
